import React, { useMemo, useState } from "react";
import { Box, Button } from "@mui/material";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";
import { MultiSelectControlledOption } from "components";
import { AllowedValuesDTO, CollectorDTO, ConfigParameterDTO, DefaultChartDTO, SiteDTO } from "api";

export const AllSelects = (
  {
    setSearchFilters,
    currentMainTab,
    allowedValues,
    sitesData,
    configParameter,
    collectorsData
  }: {
    setSearchFilters: (param: DefaultChartDTO) => void;
    currentMainTab: string | null;
    allowedValues: AllowedValuesDTO | undefined;
    sitesData: SiteDTO[] | undefined;
    configParameter: ConfigParameterDTO[] | undefined;
    collectorsData: CollectorDTO[] | undefined;
  }
) => {
  const [currentRegions, setCurrentRegions] = useState<MultiSelectControlledOption[]>([]);
  const [currentCountries, setCurrentCountries] = useState<MultiSelectControlledOption[]>([]);
  const [currentFacilityTypes, setCurrentFacilityTypes] = useState<MultiSelectControlledOption[]>([]);
  const [currentBusinessUnits, setCurrentBusinessUnits] = useState<MultiSelectControlledOption[]>([]);
  const [currentScopes, setCurrentScopes] = useState<MultiSelectControlledOption[]>([]);
  const [currentUtilityTypes, setCurrentUtilityTypes] = useState<MultiSelectControlledOption[]>([]);
  const [currentSites, setCurrentSites] = useState<MultiSelectControlledOption[]>([]);
  const [currentAccountNumbers, setCurrentAccountNumbers] = useState<MultiSelectControlledOption[]>([]);
  const [currentMeterNumbers, setCurrentMeterNumbers] = useState<MultiSelectControlledOption[]>([]);
  const [searchActive, setSearchActive] = useState<boolean>(false);
  const [defaultObject, setDefaultObject] = useState<DefaultChartDTO>({
    countryRegions: null,
    countries: null,
    siteIds: null,
    facilityTypes: null,
    businessUnits: null,
    scopes: null,
    utilityTypeIds: null,
    accountNumbers: null,
    meterNumbers: null,
  });

  const regions = useMemo(() => allowedValues ? allowedValues.countryRegions.map((reg: string) => ({ name: reg, label: reg, value: reg })) : [], [allowedValues]);
  const countries = useMemo(() => allowedValues ? allowedValues.countries.map((coun: string) => ({ name: coun.replaceAll("_", " "), label: coun, value: coun })) : [], [allowedValues]);
  const facilityTypes = useMemo(() => allowedValues ? allowedValues.siteTypes.map((site: string) => ({ name: site, label: site, value: site })) : [], [allowedValues]);
  const businessUnits = useMemo(() => allowedValues ? allowedValues.businessUnits.map((bunit: string) => ({ name: bunit, label: bunit, value: bunit })) : [], [allowedValues]);
  const scopes = useMemo(() => configParameter
    ? configParameter
      .filter((param) => param.name.includes("scope") && param.configValue === "true")
      .map((param) => param.name.replace(/\b\w/g, (char: string) => char.toUpperCase())
        .replace(/(\D)(\d+)/g, '$1 $2'))
      .filter((scope: string) => scope !== "Scope 3")
      .map((scope: string) => ({ name: scope, label: scope, value: scope.toLowerCase().replaceAll(" ", "") }))
    : [], [configParameter]
  );
  const utilityTypes = useMemo(() => allowedValues ? allowedValues.utilityTypes.map(({ name, id }) => ({ name, label: name, value: id })) : [], [allowedValues]);
  const sites = useMemo(() => sitesData
    ? sitesData
      .filter(({ name, id }) => name !== null && name !== undefined && id !== null && id !== undefined)
      .map(({ name, id }) => ({
        name: String(name),
        label: String(name),
        value: id as string | number,
      }))
    : [], [sitesData]);
  const accountNumbers = useMemo(() => collectorsData ? collectorsData.filter(({ accountNumber }) => accountNumber !== null).map(({ accountNumber }) => ({ name: accountNumber, label: accountNumber, value: accountNumber })) : [], [collectorsData]);
  const meterNumbers = useMemo(() => collectorsData
    ? collectorsData
      .filter(({ meterNumber }) => meterNumber !== null && meterNumber !== undefined)
      .map(({ meterNumber }) => ({
        name: String(meterNumber),
        label: String(meterNumber),
        value: meterNumber as string | number,
      }))
    : [], [collectorsData]);

  const handleClearFilters = () => {
    setCurrentRegions([]);
    setCurrentCountries([]);
    setCurrentFacilityTypes([]);
    setCurrentBusinessUnits([]);
    setCurrentScopes([]);
    setCurrentUtilityTypes([]);
    setCurrentSites([]);
    setCurrentAccountNumbers([]);
    setCurrentMeterNumbers([]);
    setDefaultObject({
      countryRegions: null,
      countries: null,
      siteIds: null,
      facilityTypes: null,
      businessUnits: null,
      scopes: null,
      utilityTypeIds: null,
      accountNumbers: null,
      meterNumbers: null,
    });
    setSearchActive(true);
  };

  const updateArr = (arr: MultiSelectControlledOption[], value: string | number) => {
    if (value === 'ALL') return null;

    const currentValues = arr.map((item) => item.value);

    const isSelected = currentValues.includes(value);

    const updatedValues = isSelected
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    return updatedValues.length > 0 ? updatedValues : null;
  };

  const onEmissionsTab = currentMainTab === "GHG Emissions";
  const onChangeFnHandler = (objKey: string, currentVal: MultiSelectControlledOption[], val: string | number) => {
    setDefaultObject({
      ...defaultObject,
      [objKey]: updateArr(currentVal, val),
    });
    setSearchActive(true);
  }

  const filtersConfigData = [
    {
      key: 'regionFilter',
      label: 'Region',
      selectedValues: currentRegions,
      setSelectedValues: setCurrentRegions,
      options: regions,
      onChangeFn: (value: string | number) => {
        onChangeFnHandler('countryRegions', currentRegions, value)
      }
    },
    {
      key: "countryFilter",
      label: "Country",
      selectedValues: currentCountries,
      setSelectedValues: setCurrentCountries,
      options: countries,
      onChangeFn: (value: string | number) => {
        onChangeFnHandler('countries', currentCountries, value)
      }
    },
    {
      key: "facilityTypesFilter",
      label: "Facility Types",
      selectedValues: currentFacilityTypes,
      setSelectedValues: setCurrentFacilityTypes,
      options: facilityTypes,
      onChangeFn: (value: string | number) => {
        onChangeFnHandler('facilityTypes', currentFacilityTypes, value)
      }
    },
    {
      key: "businessUnitFilter",
      label: "Business Unit",
      selectedValues: currentBusinessUnits,
      setSelectedValues: setCurrentBusinessUnits,
      options: businessUnits,
      onChangeFn: (value: string | number) => {
        onChangeFnHandler('businessUnits', currentBusinessUnits, value)
      }
    },
    {
      key: "scopeFilter",
      label: "Scope",
      selectedValues: currentScopes,
      setSelectedValues: setCurrentScopes,
      options: onEmissionsTab ? scopes : [...scopes, { name: "No Scope", label: "No Scope", value: "NO_SCOPE" }],
      onChangeFn: (value: string | number) => {
        onChangeFnHandler('scopes', currentScopes, value)
      }
    },
    {
      key: "utilityTypeFilter",
      label: "Utility Type",
      selectedValues: currentUtilityTypes,
      setSelectedValues: setCurrentUtilityTypes,
      options: utilityTypes,
      onChangeFn: (value: string | number) => {
        onChangeFnHandler('utilityTypeIds', currentUtilityTypes, value)
      }
    },
    {
      key: "sitesFilter",
      label: "Sites",
      selectedValues: currentSites,
      setSelectedValues: setCurrentSites,
      options: sites,
      onChangeFn: (value: string | number) => {
        onChangeFnHandler('siteIds', currentSites, value)
      }
    },
    {
      key: "accountNumberFilter",
      label: "Account Number",
      selectedValues: currentAccountNumbers,
      setSelectedValues: setCurrentAccountNumbers,
      options: accountNumbers,
      onChangeFn: (value: string | number) => {
        onChangeFnHandler('accountNumbers', currentAccountNumbers, value)
      }
    },
    {
      key: "meterNumberFilter",
      label: "Meter Number",
      selectedValues: currentMeterNumbers,
      setSelectedValues: setCurrentMeterNumbers,
      options: meterNumbers,
      onChangeFn: (value: string | number) => {
        onChangeFnHandler('meterNumbers', currentMeterNumbers, value)
      }
    },
  ];

  return (
    <Box sx={{
      display: "flex",
      width: "100%",
      flexWrap: "wrap",
      gap: "12px",
      paddingRight: "48px",
      mt: 3,
      "& > div": {
        width: "200px",
      }
    }}>
      {filtersConfigData.map(({ key, label, selectedValues, setSelectedValues, options, onChangeFn }) =>
        <EsgMultiselectControlledField
          key={key}
          label={label}
          selectedValues={selectedValues}
          setSelectedValues={setSelectedValues}
          options={options}
          isAllSelectedOnEmptySelection
          type="dashboard"
          onchangeFn={onChangeFn}
        />
      )}
      <Box sx={{
        width: "292px !important",
        display: "flex",
        gap: "12px",
        mt: "22px",
      }}>
        <Button
          variant={searchActive ? "contained" : "outlined"}
          onClick={() => {
            setSearchFilters(defaultObject);
            setSearchActive(false);
          }}
          sx={{ borderRadius: "8px", width: "140px" }}
        >
          Search
        </Button>
        <Button variant="outlined" onClick={handleClearFilters} sx={{ borderRadius: "8px", width: "140px" }} >
          Clear Filters
        </Button>
      </Box>
    </Box>
  )
};
